import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import React, { FC, SyntheticEvent, useState } from "react";
import { Alert, AlertTitle } from "@material-ui/lab";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { StripeCardElement } from "@stripe/stripe-js";
import { useDispatch, useSelector } from "react-redux";

import BaseInput from "../../../../../components/elements/BaseInput";
import Button from "../../../../../components/elements/Button";
import api from "../../../../../api";
import classes from "../../AdvertiserDetailsSection.module.scss";
import { ReduxApplicationState } from "../../../../../types/ReduxTypes/ReduxApplicationState";
import { setAgencyAdvertisers } from "../../../../../redux/reducers/agency";
import { successToaster } from "../../../../../helpers/toasters";

type AddPaymentMethodModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  advertiserId: string;
  fetchAndSetAdvertiserDetailData: () => void;
};

type FormInitialValues = {
  cardFriendlyName: string;
  billingEmail: string;
  cardholderName: string;
};

const createStripeIntentToken = async () => {
  try {
    const { payload } = await api.stripeIntent();
    return payload;
  } catch (error) {
    const err = error as any;
    throw new Error(err.message);
  }
};

const updateAdvertiserPaymentMethod = async (
  options: {
    paymentMethodId: string;
    friendlyName: string;
    billingEmail: string;
  },
  bgAdvertiserId: string,
) => {
  const { paymentMethodId, friendlyName, billingEmail } = options;

  try {
    const body = {
      bgAdvertiserId,
      billingEmail,
      friendlyName,
      method: paymentMethodId,
    };
    await api.advertiserStripeSave(body);
  } catch (error) {
    const err = error as any;
    throw new Error(err.message);
  }
};

const AddPaymentMethodModal: FC<AddPaymentMethodModalProps> = ({
  isOpen,
  handleClose,
  advertiserId,
  fetchAndSetAdvertiserDetailData,
}) => {
  const agencyId = useSelector((state: ReduxApplicationState) => state.agency.agencyId);
  const dispatch = useDispatch();
  const [formErrorMessage, setFormErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValues] = useState<FormInitialValues>({
    cardFriendlyName: "",
    billingEmail: "",
    cardholderName: "",
  });

  const stripe = useStripe();
  const elements = useElements();
  const cardElement = elements!.getElement(CardElement) as StripeCardElement;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues({
      ...formValues,
      [event.target.name]: event.target.value,
    });
  };

  const handleResetForm = () => {
    setFormValues({
      cardFriendlyName: "",
      billingEmail: "",
      cardholderName: "",
    });
    setFormErrorMessage("");
  };

  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();

    setIsLoading(true);
    const { cardFriendlyName, billingEmail, cardholderName } = formValues;

    try {
      const intentToken = await createStripeIntentToken();
      const { setupIntent, error: stripeFormError } = await stripe!.confirmCardSetup(intentToken, {
        payment_method: {
          billing_details: {
            name: cardholderName,
          },
          card: cardElement,
        },
      });

      if (setupIntent) {
        const paymentMethodId = setupIntent?.payment_method ?? "";
        await updateAdvertiserPaymentMethod(
          {
            paymentMethodId,
            friendlyName: cardFriendlyName,
            billingEmail,
          },
          advertiserId,
        );
        await fetchAndSetAdvertiserDetailData();
        dispatch(setAgencyAdvertisers(agencyId));
        setIsLoading(false);
        successToaster("New Payment Method Was Successfully Added!", 5000);
        handleResetForm();
        handleClose();
      }

      if (stripeFormError) {
        setIsLoading(false);
        if (stripeFormError.message) {
          setFormErrorMessage(stripeFormError.message);
        } else {
          handleResetForm();
          setFormErrorMessage("There was an unexpected error adding this card, please try again.");
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error handleSubmit: ", error);
      const err = error as any;
      setIsLoading(false);
      if (err.message) {
        setFormErrorMessage(err.message);
      } else {
        setFormErrorMessage("There was an unexpected error adding this card, please try again.");
      }
    }
  };

  const handleCloseAndResetForm = () => {
    handleResetForm();
    handleClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleCloseAndResetForm}
      aria-labelledby="Add Payment Method Modal"
      className={classes.addPaymentMethodModalContainer}
    >
      <div>
        <div className={classes.dialogTitle}>
          <Typography variant="h2">Add Payment Method</Typography>
        </div>

        <form onSubmit={handleSubmit}>
          <DialogContent className={classes.dialogContent}>
            <div className={classes.input}>
              <BaseInput
                value={formValues.cardFriendlyName}
                onChange={handleInputChange}
                name="cardFriendlyName"
                variant="outlined"
                label="Friendly Card Name (ex. BrandGen Card)"
                required
              />
            </div>

            <div className={classes.input}>
              <BaseInput
                value={formValues.billingEmail}
                onChange={handleInputChange}
                name="billingEmail"
                variant="outlined"
                label="Billing Email"
                type="email"
                required
              />
            </div>

            <div className={classes.input}>
              <BaseInput
                value={formValues.cardholderName}
                onChange={handleInputChange}
                name="cardholderName"
                variant="outlined"
                label="Cardholder Name"
                required
              />
            </div>

            <div className={classes.input}>
              <div className={classes.cardElementWrapper}>
                <CardElement />
              </div>

              {formErrorMessage && (
                <Alert severity="error" className={classes.formErrorMessage}>
                  <AlertTitle>Card Error</AlertTitle>
                  {formErrorMessage}
                </Alert>
              )}
            </div>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseAndResetForm} variant="outlined">
              Cancel
            </Button>
            <Button type="submit" disabled={!stripe} isLoading={isLoading} loaderSize={25}>
              Add
            </Button>
          </DialogActions>
        </form>
      </div>
    </Dialog>
  );
};

export default AddPaymentMethodModal;
